import React, { useEffect, useMemo, useRef, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableActions from "./table-actions";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { IEmployee } from "src/types/employees";
import { useSearchParams } from "react-router-dom";
import { rolesList } from "../../add-edit-employee/constants";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import { useGetCitiesList } from "src/api/general";
import { useGetLocationsList } from "src/api/locations";

const tableHeadText = [
  "National ID",
  "Name in English",
  "Name in Arabic",
  "Role",
  "Cities",
  "Type",
  "Email",
  "Mobile Number",
  "Actions",
];

const ArchivedEmployeesTable = ({
  rowsList,
  isLoading,
}: {
  rowsList: IEmployee[];
  isLoading: boolean;
}) => {
  // Initialize state from URL
  const [searchParams, setSearchParams] = useSearchParams();
  const roleParam = searchParams.get("role") ?? "all";
  const searchByParam = searchParams.get("search") ?? "";
  const pageParam = Number(searchParams.get("page")) || 0;
  const typeParam = searchParams.get("empType") ?? "all";
  const cityIdParam = searchParams.get("cityId");
  const locIdParam = searchParams.get("locationId");
  const empModeParam = searchParams.get("mode") ?? "all";
  const accTypeParam = searchParams.get("accType") ?? "all";

  const [page, setPage] = useState(pageParam);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchBy, setSearchBy] = useState<string>(searchByParam);
  const [tempSearchBy, setTempSearchBy] = useState<string>(searchByParam);
  const [role, setRole] = useState(roleParam);
  const [empType, setEmpType] = useState(typeParam);
  const [tempEmpType, setTempEmpType] = useState(typeParam);
  const [tempRole, setTempRole] = useState(roleParam);
  const [cityId, setCityId] = useState<string | null>(cityIdParam);
  const [tempCityId, setTempCityId] = useState<string | null>(cityIdParam);
  const [location, setLocation] = useState<any>(
    locIdParam ? { id: locIdParam } : null,
  );
  const [tempLocation, setTempLocation] = useState<any>(
    locIdParam ? { id: locIdParam } : null,
  );
  const [tempEmpMode, setTempEmpMode] = useState(empModeParam);
  const [empMode, setEmpMode] = useState(empModeParam);
  const [accType, setAccType] = useState(accTypeParam);
  const [tempAccType, setTempAccType] = useState(accTypeParam);

  const prev = useRef({
    role,
    empType,
    empMode,
    accType,
    searchBy,
    cityId,
    location,
  });

  // apis
  const { data: citiesData } = useGetCitiesList({
    type: "active",
  });
  const { data: locationsData } = useGetLocationsList();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (searchedVal: string) => {
    setTempSearchBy(searchedVal);
  };

  const handleChangeEmpModeType = (e: SelectChangeEvent) => {
    setTempEmpMode(e.target.value);
  };

  const handleApplyFilters = () => {
    setEmpType(tempEmpType);
    setRole(tempRole);
    setSearchBy(tempSearchBy);
    setCityId(tempCityId);
    setLocation(tempLocation);
    setEmpMode(tempEmpMode);
    setAccType(tempAccType);
  };

  const handleReset = () => {
    setTempEmpType("all");
    setTempRole("all");
    setTempSearchBy("");
    setCityId(null);
    setLocation(null);
    setTempEmpMode("all");
    setTempAccType("all");

    setPage(0);
    setEmpType("all");
    setRole("all");
    setSearchBy("");
    setTempCityId(null);
    setTempLocation(null);
    setEmpMode("all");
    setAccType("all");
  };

  const clearSearch = () => {
    setTempSearchBy("");
  };

  const handleChangeEmployeeType = (e: SelectChangeEvent) => {
    setTempEmpType(e.target.value);
  };

  const handleChangeRole = (e: SelectChangeEvent) => {
    setTempRole(e.target.value);
  };

  const handleChangeSelectedAccountType = (e: SelectChangeEvent) => {
    setTempAccType(e.target.value);
  };

  const handleChangeSelectedLocation = (
    event: React.SyntheticEvent,
    newValue: any,
  ) => {
    setTempLocation(newValue);
    setTempCityId(null);
  };

  const handleChangeSelectedCity = (
    event: React.SyntheticEvent,
    newValue: any | null,
  ) => {
    setTempCityId(newValue?.id ?? null);
  };

  const handleSearchAndFilters = () => {
    let searchAndFiltersRows = [...rowsList];
    //search by name, email, national id or Phone
    if (searchBy.trim()) {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return (
          row.name?.toLowerCase().includes(searchBy.toLowerCase()) ||
          row.email?.toLowerCase().includes(searchBy.toLowerCase()) ||
          row.nationalId?.toLowerCase().includes(searchBy.toLowerCase()) ||
          row.phone?.toLowerCase().includes(searchBy.toLowerCase())
        );
      });
      searchAndFiltersRows = filteredRows;
    }
    //filter by role
    if (role === "Admin") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.roles.includes("Admin");
      });
      searchAndFiltersRows = filteredRows;
    } else if (!!role && role !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row: any) => {
        return row.locationType === role;
      });
      searchAndFiltersRows = filteredRows;
    }
    //filter by selected account type
    if (accType !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.accountType === accType;
      });
      searchAndFiltersRows = filteredRows;
    }
    //filter by employee type
    if (empType !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.employeeType === empType;
      });
      searchAndFiltersRows = filteredRows;
    }
    //filter by emp mode
    if (empMode !== "all") {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return empMode === "support" ? row.isSupportMode : !row.isSupportMode;
      });
      searchAndFiltersRows = filteredRows;
    }
    // filter by city
    if (cityId) {
      searchAndFiltersRows = searchAndFiltersRows.filter((row: any) =>
        row.cities?.some((item: any) => item.id === cityId),
      );
    }
    //filter by location
    if (location) {
      const filteredRows = searchAndFiltersRows.filter((row) => {
        return row.allowedLocationsId?.includes(location.id);
      });
      searchAndFiltersRows = filteredRows;
    }
    return searchAndFiltersRows;
  };

  const searchAndFiltersList = handleSearchAndFilters();

  const paginatedVisibleRows = useMemo(
    () =>
      searchAndFiltersList.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [
      page,
      rowsPerPage,
      rowsList,
      searchBy,
      role,
      empType,
      accType,
      empMode,
      cityId,
      location,
    ],
  );

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];

    if (!tempLocation) {
      return citiesData.data;
    }

    const availableLocations = tempLocation ? [tempLocation] : sortedLocations;

    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );

    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [tempLocation, citiesData, sortedLocations]);

  const selectedCity =
    filteredCities.find((city) => city.id === tempCityId) ?? null;

  const emptyDataArr = searchAndFiltersList.length === 0 && !isLoading;

  useEffect(() => {
    const changed =
      prev.current.role !== role ||
      prev.current.empType !== empType ||
      prev.current.accType !== accType ||
      prev.current.empMode !== empMode ||
      prev.current.searchBy !== searchBy ||
      prev.current.cityId !== cityId ||
      prev.current.location !== location;
    if (changed) {
      setPage(0);
    }

    prev.current = {
      role,
      empType,
      accType,
      empMode,
      searchBy,
      cityId,
      location,
    };
  }, [role, empType, accType, empMode, searchBy, cityId, location]);

  // To Restore location filter
  useEffect(() => {
    const locationId = searchParams.get("locationId");
    if (locationId && sortedLocations.length) {
      const found = sortedLocations.find((loc: any) => loc.id === locationId);
      setLocation(found);
      setTempLocation(found);
    }
  }, [sortedLocations]);

  // To Sync URL when filters change
  useEffect(() => {
    setSearchParams({
      search: searchBy,
      role,
      empType,
      accType,
      mode: empMode,
      page: page.toString(),
      cityId: cityId ?? "",
      locationId: location?.id ?? "",
    });
  }, [searchBy, role, empType, accType, empMode, page, cityId, location]);

  return (
    <>
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={3}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          <TextField
            size="small"
            placeholder="Search"
            value={tempSearchBy}
            sx={{ width: { xs: "100%", sm: "230px" } }}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ opacity: !tempSearchBy ? 0 : 1 }}
                >
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: "180px" }}>
            <InputLabel id="demo-simple-select-label">Role</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempRole}
              label="Role"
              onChange={handleChangeRole}
            >
              <MenuItem value={"all"}>All</MenuItem>
              {rolesList.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: "130px" }}>
            <InputLabel id="demo-simple-select-label">Account Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempAccType}
              label="Account Type"
              onChange={handleChangeSelectedAccountType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"regular"}>Employee</MenuItem>
              <MenuItem value={"sunmi"}>Sunmi</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: "130px" }}>
            <InputLabel id="demo-simple-select-label">Mode</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempEmpMode}
              label="Mode"
              onChange={handleChangeEmpModeType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"support"}>Support</MenuItem>
              <MenuItem value={"regular"}>Regular</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: "130px" }}>
            <InputLabel id="demo-simple-select-label">Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempEmpType}
              label="Type"
              onChange={handleChangeEmployeeType}
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"permanent"}>Permanent</MenuItem>
              <MenuItem value={"temporary"}>Temporary</MenuItem>
            </Select>
          </FormControl>
          {/* locations */}
          <CustomAutocomplete
            size="small"
            sx={{ width: "200px" }}
            fullWidth
            disablePortal
            id="combo-box-demo"
            options={sortedLocations}
            getOptionLabel={(option: any) => option.nameEn ?? ""}
            getOptionKey={(option: any) => option.id}
            value={tempLocation}
            onChange={handleChangeSelectedLocation}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Location"} />
            )}
          />
          {/* city */}
          <CustomAutocomplete
            key={tempCityId ?? "city-autocomplete"}
            sx={{ width: { xs: "100%", sm: "170px" } }}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={selectedCity}
            onChange={handleChangeSelectedCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />
        </Stack>
        <Stack direction={"row"}>
          <Button onClick={handleReset}>Clear</Button>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            sx={{ width: "100px" }}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
      <TableContainer
        sx={{
          boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
          padding: "30px",
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {tableHeadText.map((item) => (
                <TableCell
                  key={item}
                  sx={{
                    fontWeight: 600,
                    color: "#A1A5B7",
                  }}
                >
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableLoadingSkeleton rowsLength={7} cellsLength={9} />
          ) : (
            <TableBody>
              {paginatedVisibleRows?.map((row) => {
                const role = row?.isSuperAdmin
                  ? "Super Admin"
                  : row?.isAdmin
                    ? "Admin"
                    : row.locationType;
                return (
                  <TableRow
                    key={row.employeeId}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell>{row.nationalId}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.arabicName}</TableCell>
                    <TableCell sx={{ maxWidth: "250px" }}>{role}</TableCell>
                    <TableCell>
                      {row?.cities?.map((city: any) => city.name).join(", ")}
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {row.employeeType}
                    </TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>
                      <TableActions
                        rowId={row.employeeId}
                        userRole={role}
                        accountType={row.accountType}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>

        {emptyDataArr && <NoData />}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={searchAndFiltersList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </>
  );
};
export default ArchivedEmployeesTable;
